import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { ThirdPartyPaymentAuthorizationDtoIn } from '@/models/in/ThirdPartyPaymentAuthorizationDtoIn';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';

const QUERY_URL = 'management/third-party-payment-authorizations';

interface DeleteAuthorizationDialogProps {
  open: boolean;
  onClose: () => void;
  authorization: ThirdPartyPaymentAuthorizationDtoIn;
  onSuccess: () => void;
}

const DeleteAuthorizationDialog = ({
  open,
  onClose,
  authorization,
  onSuccess,
}: DeleteAuthorizationDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: deleteAuthorization, isPending: isDeleting } = useDeleteAuthApi<void>(
    `${QUERY_URL}/${authorization.id}`
  );

  const handleDelete = () => {
    setError(null);
    deleteAuthorization(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('Error deleting third-party payment authorization:', err);
        setError(t('thirdPartyAuthorizations.dialog.errorDeleting'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const name = authorization.contactName ?? authorization.fundName ?? '';
  const message = error
    ? `${t('thirdPartyAuthorizations.dialog.confirmDelete', { name })}\n\n${error}`
    : t('thirdPartyAuthorizations.dialog.confirmDelete', { name });

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('thirdPartyAuthorizations.delete')}
      message={message}
      confirmText={isDeleting ? 'saving' : 'thirdPartyAuthorizations.dialog.deleteConfirm'}
      cancelText="thirdPartyAuthorizations.dialog.deleteCancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteAuthorizationDialog;
